'use strict';

const libPictProvider = require('pict-provider');

const BODY_MOUNT = '#UCW-Body';
const MESSAGE_MOUNT = '#UCW-Message';

// The editable User fields offered at CREATE, grouped for the form. A curated subset of the User columns — the
// raw manifest create form exposed internal columns (Password Reset Key, GUID, audit) that don't belong on a
// "new user" screen. Name is helpful but not required; LoginID + Email are required (a user needs a login and a
// contact address). IDCustomer is NEVER sent (the server stamps it from the session). LoginPassword is a real
// User column but is NOT set by the User POST — it rides a separate POST /1.0/Password after create (see saveAsync).
const FIELD_GROUPS =
[
	{ Name: 'Identity', Fields:
		[
			{ Key: 'NameFirst', Label: 'First Name', Type: 'text' },
			{ Key: 'NameLast', Label: 'Last Name', Type: 'text' },
			{ Key: 'Email', Label: 'Email', Type: 'text', Wide: true, Required: true },
			{ Key: 'LoginID', Label: 'Login ID', Type: 'text', Required: true },
			{ Key: 'Phone', Label: 'Phone', Type: 'text' },
		] },
	{ Name: 'Role & Organization', Fields:
		[
			{ Key: 'IDRole', Label: 'Role', Type: 'entity', Entity: 'Role', Wide: true },
			{ Key: 'Title', Label: 'Title', Type: 'text' },
			{ Key: 'Classification', Label: 'Classification', Type: 'text' },
			{ Key: 'IDProjectOffice', Label: 'Project Office ID', Type: 'number' },
		] },
	{ Name: 'Security', Fields:
		[
			{ Key: 'LoginPassword', Label: 'Login Password', Type: 'password', Wide: true },
		] },
	{ Name: 'Status', Fields:
		[
			{ Key: 'Disabled', Label: 'Disabled (cannot sign in)', Type: 'bool' },
		] },
];

// Server-side entity-picker config per relationship field (this app has no pict.EntityProvider — the picker
// searches on the server through HeadlightRestClient, mirroring ProjectCreateWorkspace). Roles are NOT scoped by
// IDCustomer here (platform + customer roles are both assignable; the server scopes what the session can read).
const PICKER_PAGE_SIZE = 25;
const ENTITY_CONFIG =
{
	IDRole: { Set: 'Roles', One: 'Role', IDField: 'IDRole', Noun: 'role', CustomerScoped: false, SearchFields: [ 'Name', 'Description', 'Type' ], IgnoreFields: [ 'IDCustomer', 'GUIDRole' ] },
};

/**
 * UserCreateWorkspace — the data layer behind the bespoke User CREATE screen. Reached by the app-router Create
 * bridge (#/PSRS/User/Create) that shadows the generic recordset create form, so "+ New" opens a curated,
 * grouped experience instead of the plain manifest form (which exposed raw columns like Password Reset Key).
 *
 * It starts from a blank working copy, POSTs a new User (IDCustomer stamped by the server — never sent), then —
 * because the User POST does NOT establish the login credential — fires the separate POST /1.0/Password with the
 * new IDUser when a password was entered (mirroring the create-time password fix + the Reset Password action).
 * On success it navigates to #/PSRS/User/View/<newGUID>, which the View bridge opens as the bespoke UserWorkspace.
 */
class UserCreateWorkspaceProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get modal()
	{
		return this.pict.providers['Pict-Section-Modal'] || (this.pict.views && this.pict.views.PictSectionModal) || null;
	}

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get state()
	{
		if (!this.pict.AppData.UserCreateWorkspace)
		{
			this.pict.AppData.UserCreateWorkspace =
			{
				Ready: false, Message: '', MessageClass: '', Saving: false,
				IDCustomer: 0, Fields: {}, Errors: {},
			};
		}
		return this.pict.AppData.UserCreateWorkspace;
	}

	// --- REST helpers ---

	_create(pRecord)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.createEntity('User', pRecord, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/**
	 * Set the login password on the freshly-created user. POST /1.0/Password { IDUser, NewPassword } is the ONLY
	 * call that establishes the login credential (the User column does not) — a raw fetch, since the JSON rest
	 * client can't carry this bespoke endpoint (mirrors PlatformConfiguration-Application-Web._setUserPasswordAsync
	 * and the UserWorkspace Reset Password action).
	 */
	_setUserPasswordAsync(pIDUser, pPassword)
	{
		const tmpPrefix = (this.restClient && this.restClient.options && this.restClient.options.ServerURL) || '/1.0/';
		return fetch(`${tmpPrefix}Password`,
			{
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ IDUser: pIDUser, NewPassword: pPassword }),
			})
			.then((pResponse) =>
			{
				if (!pResponse.ok) { throw new Error(`server returned ${pResponse.status}`); }
				return pResponse.json().catch(() => null).then((pBody) =>
				{
					if (pBody && (pBody.Success === false)) { throw new Error(pBody.Error || 'password not set'); }
					return pBody;
				});
			});
	}

	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	_toNumberOrNull(pValue)
	{
		if (pValue == null || String(pValue).trim() === '') { return null; }
		const tmpNumber = Number(pValue);
		return Number.isFinite(tmpNumber) ? tmpNumber : null;
	}

	// --- Start a new user ---

	/** Reset to a blank working copy and paint the empty form. Driven by the Create route bridge. */
	startNew()
	{
		const tmpState = this.state;
		tmpState.Message = '';
		tmpState.MessageClass = '';
		tmpState.Saving = false;
		tmpState.Errors = {};
		tmpState.IDCustomer = Number(this.currentUser.CustomerID) || 0;
		const tmpFields = {};
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			// Booleans start false; entity FKs start unset as '' (not 0) so the picker shows its placeholder.
			tmpFields[pField.Key] = (pField.Type === 'bool') ? false : '';
		}));
		tmpState.Fields = tmpFields;
		tmpState.Ready = true;
		this.render();
	}

	// --- Editing ---

	/** Update one working-copy field. onchange-driven, so no re-render (the DOM already holds the value). */
	setField(pKey, pValue)
	{
		this.state.Fields[pKey] = pValue;
		if (this.state.Errors[pKey]) { delete this.state.Errors[pKey]; }
	}

	setBool(pKey, pChecked)
	{
		this.state.Fields[pKey] = !!pChecked;
	}

	_validate()
	{
		const tmpState = this.state;
		const tmpErrors = {};
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			const tmpValue = tmpState.Fields[pField.Key];
			const tmpHasValue = (tmpValue != null && String(tmpValue).trim() !== '');
			if (pField.Required && !tmpHasValue) { tmpErrors[pField.Key] = `${pField.Label} is required.`; return; }
			if (pField.Type === 'number' && tmpHasValue)
			{
				const tmpNumber = Number(tmpValue);
				if (!Number.isFinite(tmpNumber)) { tmpErrors[pField.Key] = `${pField.Label} must be a number.`; }
			}
		}));
		// A supplied password must meet the server minimum (>= 2 chars); blank is allowed (created password-less).
		const tmpPassword = tmpState.Fields.LoginPassword;
		if (tmpPassword != null && String(tmpPassword) !== '' && String(tmpPassword).length < 2)
		{
			tmpErrors.LoginPassword = 'The password must be at least 2 characters (leave blank to set it later).';
		}
		tmpState.Errors = tmpErrors;
		return Object.keys(tmpErrors).length === 0;
	}

	// --- Save / navigation ---

	async saveAsync()
	{
		const tmpState = this.state;
		if (tmpState.Saving) { return; }
		if (!this._validate())
		{
			tmpState.Message = 'Please fix the highlighted fields.';
			tmpState.MessageClass = 'is-danger';
			this.render();
			return;
		}
		tmpState.Saving = true;
		tmpState.Message = 'Creating…';
		tmpState.MessageClass = 'is-info';
		this._renderMessage();
		try
		{
			// Build the create payload — omit empty optionals so the server applies its own defaults. LoginPassword
			// is stripped (set via POST /Password after create); IDCustomer is never sent (stamped from the session).
			const tmpPassword = (tmpState.Fields.LoginPassword == null) ? '' : String(tmpState.Fields.LoginPassword);
			const tmpPayload = {};
			FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
			{
				if (pField.Key === 'LoginPassword') { return; }
				const tmpValue = tmpState.Fields[pField.Key];
				if (pField.Type === 'bool')
				{
					tmpPayload[pField.Key] = tmpValue ? 1 : 0;
				}
				else if ((pField.Type === 'number') || (pField.Type === 'entity'))
				{
					const tmpNumber = this._toNumberOrNull(tmpValue);
					if (tmpNumber != null) { tmpPayload[pField.Key] = tmpNumber; }
				}
				else if (tmpValue != null && String(tmpValue).trim() !== '')
				{
					tmpPayload[pField.Key] = tmpValue;
				}
			}));
			const tmpSaved = await this._create(tmpPayload);
			const tmpIDUser = tmpSaved && (tmpSaved.IDUser || (tmpSaved.Record && tmpSaved.Record.IDUser));
			if (!tmpSaved || tmpSaved.ErrorCode || !tmpIDUser)
			{
				throw new Error((tmpSaved && (tmpSaved.Error || tmpSaved.ErrorCode))
					? String(tmpSaved.Error || tmpSaved.ErrorCode)
					: 'the server rejected the new user (you may not have permission to create users).');
			}
			// Second call: establish the login credential. A failure here is non-fatal — the user exists and can
			// get a password via the Reset Password action — so warn rather than error out.
			if (tmpPassword)
			{
				try { await this._setUserPasswordAsync(tmpIDUser, tmpPassword); }
				catch (pPwError)
				{
					if (this.modal && (typeof this.modal.toast === 'function'))
					{
						this.modal.toast('User created, but the login password could not be set — open the user and use "Reset Password".', { type: 'warning' });
					}
				}
			}
			tmpState.Saving = false;
			this._navigateToView(tmpSaved);
		}
		catch (pError)
		{
			tmpState.Saving = false;
			tmpState.Message = `Could not create the user: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this.render();
		}
	}

	cancel()
	{
		if ((typeof window !== 'undefined') && window.location)
		{
			window.location.hash = '#/PSRS/User/List';
		}
	}

	/** Land on the bespoke read workspace for the just-created user (via the View bridge). */
	_navigateToView(pSaved)
	{
		if ((typeof window !== 'undefined') && window.location)
		{
			window.location.hash = pSaved.GUIDUser ? `#/PSRS/User/View/${pSaved.GUIDUser}` : '#/PSRS/User/List';
		}
	}

	// --- Render (hand-built HTML into the shell's mount; mirrors ProjectCreateWorkspace-Provider) ---

	render()
	{
		if (this.pict.ContentAssignment.getElement(BODY_MOUNT).length === 0) { return; }
		this.pict.ContentAssignment.assignContent(BODY_MOUNT, this._bodyHTML());
		if (this.state.Ready) { this._mountPickers(); }
	}

	_mountPickers()
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPickerProvider || (typeof tmpPickerProvider.createPicker !== 'function')) { return; }
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			if (pField.Type === 'entity') { this._mountPicker(pField.Key); }
		}));
	}

	_mountPicker(pKey)
	{
		const tmpMount = `#UCW-Picker-${pKey}`;
		if (this.pict.ContentAssignment.getElement(tmpMount).length === 0) { return; }
		const tmpConfig = ENTITY_CONFIG[pKey];
		if (!tmpConfig) { return; }
		const tmpPicker = this.pict.providers['Pict-Section-Picker'].createPicker(`UCW-Picker-${pKey}`,
			{
				DestinationAddress: tmpMount,
				ValueAddress: `AppData.UserCreateWorkspace.Fields.${pKey}`,
				Searchable: true,
				AllowClear: true,
				ClearLabel: '(none)',
				Placeholder: `Search a ${tmpConfig.Noun}…`,
				DataProvider: this._entityDataProvider(tmpConfig),
				ResolveValue: this._entityResolveValue(tmpConfig),
				Entity: tmpConfig.One,
				AllowFieldDecoration: true,
				DecorationIgnoreFields: tmpConfig.IgnoreFields,
			});
		if (tmpPicker && (typeof tmpPicker.render === 'function')) { tmpPicker.render(); }
	}

	_entityDataProvider(pConfig)
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		return (pSearchTerm, pPage) =>
		{
			const tmpStart = (Number(pPage) || 0) * PICKER_PAGE_SIZE;
			let tmpFilter = (pConfig.CustomerScoped === false)
				? `FBV~Deleted~EQ~0`
				: `FBV~IDCustomer~EQ~${this.state.IDCustomer}~FBV~Deleted~EQ~0`;
			const tmpTerm = String(pSearchTerm == null ? '' : pSearchTerm).trim();
			if (tmpTerm && tmpPickerProvider && (typeof tmpPickerProvider.buildSearchFilter === 'function'))
			{
				tmpFilter += `~${tmpPickerProvider.buildSearchFilter(pConfig.SearchFields, tmpTerm)}`;
			}
			return this._getJSON(`${pConfig.Set}/FilteredTo/${tmpFilter}/${tmpStart}/${PICKER_PAGE_SIZE}`)
				.then((pRows) =>
				{
					const tmpList = Array.isArray(pRows) ? pRows : [];
					return { results: tmpList.map((pRow) => ({ Value: Number(pRow[pConfig.IDField]), Text: pRow.Name || `#${pRow[pConfig.IDField]}`, Record: pRow })), hasMore: (tmpList.length >= PICKER_PAGE_SIZE) };
				})
				.catch(() => ({ results: [], hasMore: false }));
		};
	}

	_entityResolveValue(pConfig)
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			if (!tmpID) { return Promise.resolve(null); }
			return this._getJSON(`${pConfig.One}/${tmpID}`)
				.then((pRow) => ((pRow && pRow[pConfig.IDField]) ? { Value: Number(pRow[pConfig.IDField]), Text: pRow.Name || `#${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` }))
				.catch(() => ({ Value: tmpID, Text: `#${tmpID}` }));
		};
	}

	_renderMessage()
	{
		if (this.pict.ContentAssignment.getElement(MESSAGE_MOUNT).length === 0) { this.render(); return; }
		this.pict.ContentAssignment.assignContent(MESSAGE_MOUNT, this._messageHTML());
	}

	_messageHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Message) { return ''; }
		return `<div class="notification ${this._esc(tmpState.MessageClass || 'is-info')} is-soft py-2 px-3 mb-3">${this._esc(tmpState.Message)}</div>`;
	}

	_bodyHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Ready)
		{
			return `<div class="pew-empty box has-text-grey has-text-centered"><span class="pew-spinner"></span><p class="mt-3">Loading…</p></div>`;
		}
		return `<div class="pew-head">`
			+ `<div><div class="pew-eyebrow">New User</div><h1 class="pew-title">Create a user</h1></div>`
			+ `<div class="pew-head-actions">`
			+ `<button class="button is-link"${tmpState.Saving ? ' disabled' : ''} onclick="_Pict.providers.UserCreateWorkspace.saveAsync(); return false;"><span>Create user</span></button>`
			+ `<button class="button is-light" onclick="_Pict.providers.UserCreateWorkspace.cancel(); return false;"><span>Cancel</span></button>`
			+ `</div></div>`
			+ `<div id="UCW-Message">${this._messageHTML()}</div>`
			+ `<div class="pew-form">${FIELD_GROUPS.map((pGroup) => this._groupHTML(pGroup)).join('')}</div>`;
	}

	_groupHTML(pGroup)
	{
		const tmpRows = pGroup.Fields.map((pField) => this._fieldHTML(pField)).join('');
		return `<div class="pew-group"><div class="pew-group-head">${this._esc(pGroup.Name)}</div><div class="pew-grid">${tmpRows}</div></div>`;
	}

	_fieldHTML(pField)
	{
		const tmpState = this.state;
		const tmpValue = tmpState.Fields[pField.Key];
		const tmpError = tmpState.Errors[pField.Key] ? `<div class="pew-field-error">${this._esc(tmpState.Errors[pField.Key])}</div>` : '';
		const tmpWide = pField.Wide ? ' pew-field-wide' : '';
		if (pField.Type === 'bool')
		{
			const tmpChecked = tmpValue ? ' checked' : '';
			return `<div class="pew-field${tmpWide}"><label class="ucw-check"><input type="checkbox"${tmpChecked} onchange="_Pict.providers.UserCreateWorkspace.setBool('${pField.Key}', this.checked)" /> <span>${this._esc(pField.Label)}</span></label>${tmpError}</div>`;
		}
		const tmpRequired = pField.Required ? ' <span class="ucw-req">*</span>' : '';
		return `<div class="pew-field${tmpWide}"><label class="pew-field-label">${this._esc(pField.Label)}${tmpRequired}</label>${this._controlHTML(pField, tmpValue)}${tmpError}</div>`;
	}

	_controlHTML(pField, pValue)
	{
		const tmpOnChange = `onchange="_Pict.providers.UserCreateWorkspace.setField('${pField.Key}', this.value)"`;
		if (pField.Type === 'entity')
		{
			return `<div id="UCW-Picker-${pField.Key}" class="pew-input pew-picker-host"></div>`;
		}
		if (pField.Type === 'password')
		{
			return `<input class="input pew-input" type="text" autocomplete="new-password" spellcheck="false" placeholder="Leave blank to set later via Reset Password" value="${this._esc(pValue)}" style="font-family: ui-monospace, SFMono-Regular, Menlo, monospace;" ${tmpOnChange} />`;
		}
		const tmpStep = (pField.Type === 'number') ? ' step="any"' : '';
		return `<input class="input pew-input" type="${(pField.Type === 'number') ? 'number' : 'text'}"${tmpStep} value="${this._esc(pValue)}" ${tmpOnChange} />`;
	}
}

module.exports = UserCreateWorkspaceProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'UserCreateWorkspace',
	AutoInitialize: true,
	AutoInitializeOrdinal: 225,
};
